import { useEffect, useMemo, useState } from "react";
import { RefreshCw, Search, ShieldCheck, Wifi } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { FibiLoading } from "../components/FibiLoading";

const fmtWaktu = (iso) => {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const statusOf = (it) => (it.live ? it.status : it.disabled ? "nonaktif" : "aktif");
const STATUS_STYLE = {
  online: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
  aktif: "bg-sky-500/10 text-sky-500 border-sky-500/20",
  active: "bg-sky-500/10 text-sky-500 border-sky-500/20",
  expired: "bg-rose-500/10 text-rose-400 border-rose-500/20",
  nonaktif: "bg-slate-500/10 text-slate-400 border-slate-500/20",
};
const STATUS_LABEL = { online: "Online", aktif: "Aktif", active: "Aktif", expired: "Kedaluwarsa", nonaktif: "Nonaktif" };

export default function SemuaWifiRumahan() {
  const { user: me } = useAuth();
  const isAdmin = ["admin", "admin_fasnet"].includes(me?.role);
  const [data, setData] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState({});
  const [massRefreshing, setMassRefreshing] = useState(false);
  const [query, setQuery] = useState("");
  const [cabang, setCabang] = useState("all");
  const [perPage, setPerPage] = useState("25");
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/ppp/all")
      .then((r) => { setData(r.data); setItems(r.data.items || []); })
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, [isAdmin]);

  const cabangOptions = useMemo(() => {
    const map = new Map();
    items.forEach((i) => map.set(i.router_name, i.router_id));
    return [...map.keys()].sort((a, b) => a.localeCompare(b));
  }, [items]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((i) => {
      if (cabang !== "all" && i.router_name !== cabang) return false;
      if (!q) return true;
      return [i.name, i.password, i.profile, i.router_name].some((f) => (f || "").toLowerCase().includes(q));
    });
  }, [items, query, cabang]);

  const per = Number(perPage);
  const pageCount = Math.max(1, Math.ceil(filtered.length / per));
  const curPage = Math.min(page, pageCount - 1);
  const rows = filtered.slice(curPage * per, curPage * per + per);

  const applyLive = (routerId, routerName, data) => {
    const liveRows = (data || []).map((s) => ({
      name: s.name, password: s.password, profile: s.profile, service: s.service,
      disabled: s.disabled, status: s.status, expired_date: s.expired_date,
      router_id: routerId, router_name: routerName, live: true,
    }));
    setItems((prev) => {
      const rest = prev.filter((i) => i.router_id !== routerId);
      return [...rest, ...liveRows].sort((a, b) => a.router_name.localeCompare(b.router_name) || a.name.localeCompare(b.name));
    });
    return liveRows.length;
  };

  const refreshLive = async (routerId, routerName) => {
    setRefreshing((p) => ({ ...p, [routerId]: true }));
    try {
      const r = await api.get(`/routers/${routerId}/ppp`);
      toast.success(`Data live ${routerName} dimuat: ${applyLive(routerId, routerName, r.data)} konsumen`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setRefreshing((p) => ({ ...p, [routerId]: false }));
    }
  };

  const refreshAllLive = async () => {
    const cabangs = [...new Map(items.map((i) => [i.router_id, i.router_name])).entries()];
    if (!cabangs.length) { toast.error("Belum ada data cabang untuk disegarkan"); return; }
    setMassRefreshing(true);
    let ok = 0, fail = 0;
    for (const [rid, name] of cabangs) {
      setRefreshing((p) => ({ ...p, [rid]: true }));
      try { const r = await api.get(`/routers/${rid}/ppp`); applyLive(rid, name, r.data); ok++; }
      catch { fail++; }
      setRefreshing((p) => ({ ...p, [rid]: false }));
    }
    setMassRefreshing(false);
    toast.success(`Refresh masal selesai: ${ok} cabang live, ${fail} gagal/offline`);
  };

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3" data-testid="swr-forbidden">
        <ShieldCheck className="w-10 h-10 text-rose-500" />
        <p className="text-muted-foreground text-sm">Hanya Admin Fasnet / Super Admin yang dapat melihat semua data WiFi Rumahan.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="semua-wifi-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
          <Wifi className="w-6 h-6 text-emerald-400" /> Semua WiFi Rumahan
        </h1>
        <p className="text-xs text-muted-foreground mt-1">Data konsumen rumahan dari seluruh cabang, diambil dari backup harian. Klik ikon refresh di kolom Cabang untuk memuat data live dari router.</p>
        {data && (
          <div className="flex items-center gap-3 flex-wrap mt-2 text-xs font-mono" data-testid="swr-summary">
            <span className="px-2.5 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400" data-testid="swr-total">Total {items.length} konsumen</span>
            <span className="px-2.5 py-1 rounded-lg bg-sky-500/10 border border-sky-500/30 text-sky-400" data-testid="swr-routers-ok">{data.routers_ok} cabang ter-backup</span>
            <span className="px-2.5 py-1 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-400" data-testid="swr-routers-failed">{data.routers_failed} cabang gagal/belum ter-backup</span>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
        <Select value={perPage} onValueChange={(v) => { setPerPage(v); setPage(0); }}>
          <SelectTrigger className="w-20" data-testid="select-swr-per-page"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["10", "25", "50", "100"].map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
          </SelectContent>
        </Select>
        <span>data per halaman</span>
        {filtered.length > per && (
          <div className="flex gap-1.5 ml-2">
            <Button size="sm" variant="outline" disabled={curPage === 0} onClick={() => setPage(curPage - 1)} data-testid="btn-swr-prev">Sebelumnya</Button>
            <Button size="sm" variant="outline" disabled={curPage >= pageCount - 1} onClick={() => setPage(curPage + 1)} data-testid="btn-swr-next">Berikutnya</Button>
          </div>
        )}
        <div className="relative ml-2">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={query} onChange={(e) => { setQuery(e.target.value); setPage(0); }} placeholder="Cari nama / password / profile / cabang..." className="pl-9 w-64" data-testid="input-swr-search" />
        </div>
        <Select value={cabang} onValueChange={(v) => { setCabang(v); setPage(0); }}>
          <SelectTrigger className="w-52" data-testid="select-swr-cabang"><SelectValue placeholder="Semua Cabang" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Cabang ({cabangOptions.length})</SelectItem>
            {cabangOptions.map((c) => <SelectItem key={c} value={c} data-testid={`swr-cabang-${c}`}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" onClick={refreshAllLive} disabled={massRefreshing || loading} title="Muat ulang data live dari semua cabang yang punya data" data-testid="btn-refresh-masal">
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${massRefreshing ? "animate-spin text-sky-500" : ""}`} /> {massRefreshing ? "Menyegarkan..." : "Refresh Live Masal"}
        </Button>
      </div>

      <Card className="bg-card border-border shadow-sm overflow-x-auto">
        <Table className="min-w-[820px]" data-testid="swr-table">
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">#</TableHead>
              <TableHead>Nama Pelanggan</TableHead>
              <TableHead>Password</TableHead>
              <TableHead>Profile</TableHead>
              <TableHead>Cabang</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={6} className="p-0" data-testid="swr-loading"><FibiLoading text="FIBI sedang memuat data..." size={20} /></TableCell></TableRow>
            )}
            {!loading && !rows.length && (
              <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground" data-testid="swr-empty">
                {items.length ? "Tidak ada data yang cocok dengan pencarian/filter." : "Belum ada data PPP secret dari backup. Jalankan backup di halaman Backup & Restore."}
              </TableCell></TableRow>
            )}
            {rows.map((it, idx) => {
              const st = statusOf(it);
              return (
                <TableRow key={`${it.router_id}-${it.name}`} data-testid={`swr-row-${it.name}`}>
                  <TableCell className="font-mono text-muted-foreground">{curPage * per + idx + 1}</TableCell>
                  <TableCell className="font-semibold">{it.name}</TableCell>
                  <TableCell className="font-mono text-xs">{it.password || "-"}</TableCell>
                  <TableCell className="font-mono text-xs">{it.profile || "-"}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm">{it.router_name}</span>
                      <Button size="icon" variant="ghost" className="h-7 w-7" title={`Refresh live dari ${it.router_name}`}
                        onClick={() => refreshLive(it.router_id, it.router_name)} disabled={refreshing[it.router_id]}
                        data-testid={`btn-swr-refresh-${it.router_name}`}>
                        <RefreshCw className={`w-3.5 h-3.5 ${refreshing[it.router_id] ? "animate-spin text-sky-500" : "text-muted-foreground"}`} />
                      </Button>
                    </div>
                    <div className="text-[10px] font-mono text-muted-foreground" data-testid={`swr-source-${it.name}`}>
                      {it.live ? "Live • baru saja" : `Backup ${fmtWaktu(it.backup_at)}`}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-mono border ${STATUS_STYLE[st] || STATUS_STYLE.aktif}`} data-testid={`swr-status-${it.name}`}>
                      {STATUS_LABEL[st] || st}
                    </span>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {filtered.length > per && (
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-border text-xs text-muted-foreground">
            <span data-testid="swr-count">Menampilkan {curPage * per + 1}–{Math.min((curPage + 1) * per, filtered.length)} dari {filtered.length} konsumen</span>
            <div className="flex gap-1.5">
              <Button size="sm" variant="outline" disabled={curPage === 0} onClick={() => setPage(curPage - 1)} data-testid="btn-swr-prev-bottom">Sebelumnya</Button>
              <Button size="sm" variant="outline" disabled={curPage >= pageCount - 1} onClick={() => setPage(curPage + 1)} data-testid="btn-swr-next-bottom">Berikutnya</Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
